import React, { useEffect, useRef } from 'react';

import { Box } from '@material-ui/core';
import i18n from 'i18n-js';

import { TableSkeleton } from 'components/shared';
import {
    formatAssignedTables,
    formatGuestName,
    formatGuestsCount,
    formatReservationDate,
    getReservationFilterKey,
    isReservationFilterActive,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import reservationsStore from 'store/reservationsStore';
import {
    ReservationsTabFilter,
    useAppliedReservationFilter,
    useReservationMaps,
    useThunkDispatch,
} from '../hooks';
import { useFirstLoad } from 'hooks/useFirstLoad';
import ReservationDetailsPanel, { ReservationStatusChip } from './ReservationDetailsPanel';
import ReservationsFilterBar from './ReservationsFilterBar';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';
import ConfirmedReservationActions from './ConfirmedReservationActions';

interface ActiveTabProps {
    filter: ReservationsTabFilter;
    onFilterChange: (filter: ReservationsTabFilter) => void;
}

export default function ActiveTab({ filter, onFilterChange }: ActiveTabProps) {
    const dispatch = useThunkDispatch();
    const { active, selectedActiveId } = useAppSelector((state) => state.reservations);
    const { tables } = useReservationMaps();
    const { applied, isDebouncing } = useAppliedReservationFilter(filter);
    const appliedKey = getReservationFilterKey(applied);
    const isFirstLoad = useRef(true);
    const shownFilter = { day: active.day ?? null, search: active.search };
    const isShownFilterApplied =
        !active.isLoading &&
        active.day !== undefined &&
        getReservationFilterKey(shownFilter) === appliedKey;
    const reservations = active.reservations;
    const selected = reservations.find((reservation) => reservation.id === selectedActiveId);

    const [isFirstLoading, settle] = useFirstLoad();

    useEffect(() => {
        dispatch(
            reservationsStore.actionCreators.getActiveReservations(applied.day, applied.search, {
                keepPrevious: !isFirstLoad.current,
                isList: true,
            })
        ).finally(settle);
        isFirstLoad.current = false;
    }, [appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isShownFilterApplied && selectedActiveId && !selected) {
            dispatch(reservationsStore.actionCreators.selectActive(null));
        }
    }, [isShownFilterApplied, selectedActiveId, !!selected]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (reservation) =>
                formatReservationDate(reservation.reservationStartDateTime, 'dd.MM.yy · HH:mm'),
            getSortValue: (reservation) => reservation.reservationStartDateTime,
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (reservation) => reservation.guestsNumber,
        },
        {
            id: 'table',
            label: i18n.t('reservations.columns.table'),
            getText: (reservation) => formatAssignedTables(reservation.tables, tables),
        },
        {
            id: 'channel',
            label: i18n.t('reservations.columns.channel'),
            getText: (reservation) => i18n.t(`reservations.channels.${reservation.channel}`),
        },
        {
            id: 'status',
            label: i18n.t('reservations.columns.status'),
            getText: (reservation) =>
                i18n.t(`reservations.status.${reservation.status.toLowerCase()}`),
            render: (reservation) => <ReservationStatusChip status={reservation.status} />,
        },
    ];

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <ReservationsFilterBar
                value={filter}
                onChange={onFilterChange}
                isLoading={isDebouncing || active.isLoading}
            />
            <Box position="relative" display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={reservations}
                    selectedId={selectedActiveId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectActive(id))}
                    emptyText={
                        isReservationFilterActive(shownFilter)
                            ? i18n.t('reservations.filters.noResults')
                            : undefined
                    }
                    isLoading={active.isLoading}
                    pageResetKey={appliedKey}
                />
                {selected && (
                    <ReservationDetailsPanel
                        key={selected.id}
                        reservation={selected}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectActive(null))
                        }
                        footer={
                            selected.status === 'Confirmed' && !selected.isArchived ? (
                                <>
                                    <ConfirmedReservationActions
                                        reservation={selected}
                                        tables={tables}
                                    />
                                    {/*<Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            history.push(`/reservations/${selected.id}/edit`)
                                        }
                                    >
                                        {i18n.t('reservations.editReservation')}
                                    </Button>*/}
                                </>
                            ) : undefined
                        }
                    />
                )}
                <TableSkeleton
                    isLoading={isFirstLoading}
                    columnWidths={[140, undefined, 80, 120, 120, 120]}
                />
            </Box>
        </Box>
    );
}
